import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  MapPin, 
  ExternalLink, 
  Award,
  DollarSign,
  UserPlus,
  Info
} from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Recommendation, QuadrantData } from "@/types/recommendation";
import { Link } from "react-router";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";

interface RecommendationMapProps {
  recommendations: Recommendation[];
  vagaId: string;
}

// Definição dos quadrantes baseada na documentação
const QUADRANTS: QuadrantData[] = [
  {
    name: "ideal_zone",
    x_range: [80, 100],
    y_range: [80, 100],
    description: "Skills excelentes + Valor competitivo",
    color: "bg-green-100 border-green-300 text-green-800"
  },
  {
    name: "high_skill_premium",
    x_range: [80, 100],
    y_range: [50, 79],
    description: "Skills excelentes + Valor premium",
    color: "bg-blue-100 border-blue-300 text-blue-800"
  },
  {
    name: "budget_friendly",
    x_range: [60, 79],
    y_range: [80, 100],
    description: "Skills adequadas + Valor competitivo",
    color: "bg-yellow-100 border-yellow-300 text-yellow-800"
  },
  {
    name: "development_potential",
    x_range: [40, 79],
    y_range: [60, 100],
    description: "Skills em desenvolvimento + Valor justo",
    color: "bg-purple-100 border-purple-300 text-purple-800"
  }
];

// Função para traduzir nomes dos quadrantes
const getQuadrantDisplayName = (name: string): string => {
  const translations: Record<string, string> = {
    'ideal_zone': 'Zona Ideal',
    'high_skill_premium': 'Skills Premium',
    'budget_friendly': 'Custo-Benefício',
    'development_potential': 'Potencial de Desenvolvimento'
  };
  return translations[name] || name.replace(/_/g, ' ');
};

// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};


// Função para determinar o quadrante de um freelancer
const getFreelancerQuadrant = (x: number, y: number): QuadrantData | null => {
  for (const quadrant of QUADRANTS) {
    if (x >= quadrant.x_range[0] && x <= quadrant.x_range[1] &&
        y >= quadrant.y_range[0] && y <= quadrant.y_range[1]) {
      return quadrant;
    }
  }
  return null;
};

// Função para obter cor do ponto baseado no score geral
const getPointColor = (overallScore: number): string => {
  if (overallScore >= 80) return "bg-green-500 border-green-600";
  if (overallScore >= 70) return "bg-blue-500 border-blue-600";
  if (overallScore >= 60) return "bg-yellow-500 border-yellow-600";
  return "bg-orange-500 border-orange-600";
};

export function RecommendationMap({ recommendations, vagaId }: RecommendationMapProps) {
  const [selectedFreelancer, setSelectedFreelancer] = useState<string | null>(null);

  // Estatísticas dos quadrantes
  const quadrantStats = useMemo(() => {
    const stats = QUADRANTS.map(quadrant => ({
      ...quadrant,
      count: 0,
      freelancers: [] as Recommendation[]
    }));

    recommendations.forEach(rec => {
      const quadrant = getFreelancerQuadrant(rec.quadrant_position.x, rec.quadrant_position.y);
      if (quadrant) {
        const stat = stats.find(s => s.name === quadrant.name);
        if (stat) {
          stat.count++;
          stat.freelancers.push(rec);
        }
      }
    });

    return stats;
  }, [recommendations]);

  return (
    <div className="space-y-6">
      {/* Legenda dos quadrantes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quadrantStats.map((quadrant) => (
          <Card key={quadrant.name} className={`border-2 ${quadrant.color.includes('green') ? 'border-green-300' : 
            quadrant.color.includes('blue') ? 'border-blue-300' :
            quadrant.color.includes('yellow') ? 'border-yellow-300' : 'border-purple-300'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span>
                  {getQuadrantDisplayName(quadrant.name)}
                </span>
                <Badge variant="outline" className="text-xs">
                  {quadrant.count}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-slate-600">{quadrant.description}</p>
              <div className="text-xs text-slate-500 mt-1">
                Skills: {quadrant.x_range[0]}%-{quadrant.x_range[1]}% • 
                Valor: {quadrant.y_range[0]}%-{quadrant.y_range[1]}%
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Mapa de quadrantes */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Mapa de Quadrantes</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-slate-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs max-w-xs">
                    Eixo X: Compatibilidade de Skills (0-100%)<br />
                    Eixo Y: Compatibilidade de Valor (0-100%)<br />
                    Clique nos pontos para ver detalhes
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative w-full h-96 border border-slate-200 rounded-lg bg-linear-to-br from-slate-50 to-white overflow-hidden">
            {/* Grid de fundo */}
            <div className="absolute inset-0">
              {/* Linhas verticais */}
              {[20, 40, 60, 80].map(x => (
                <div 
                  key={`v-${x}`}
                  className="absolute top-0 bottom-0 border-l border-slate-200 opacity-50"
                  style={{ left: `${x}%` }}
                />
              ))}
              {/* Linhas horizontais */}
              {[20, 40, 60, 80].map(y => (
                <div 
                  key={`h-${y}`}
                  className="absolute left-0 right-0 border-t border-slate-200 opacity-50"
                  style={{ bottom: `${y}%` }}
                />
              ))}
            </div>

            {/* Áreas dos quadrantes */}
            {QUADRANTS.map((quadrant) => (
              <div
                key={quadrant.name}
                className={`absolute opacity-10 ${quadrant.color.split(' ')[0]}`}
                style={{
                  left: `${quadrant.x_range[0]}%`,
                  bottom: `${quadrant.y_range[0]}%`,
                  width: `${quadrant.x_range[1] - quadrant.x_range[0]}%`,
                  height: `${quadrant.y_range[1] - quadrant.y_range[0]}%`,
                }}
              />
            ))}

            {/* Pontos dos freelancers */}
            <TooltipProvider>
              {recommendations.map((recommendation) => {
                const { freelancer, quadrant_position, scoring } = recommendation;
                const overallScore = parseFloat(scoring.overall_score);
                
                return (
                  <Tooltip key={freelancer.id}>
                    <TooltipTrigger asChild>
                      <button
                        className={`absolute w-4 h-4 rounded-full border-2 transform -translate-x-2 -translate-y-2 transition-all duration-200 hover:scale-125 hover:z-10 ${
                          selectedFreelancer === freelancer.id 
                            ? 'scale-125 z-10 ring-2 ring-blue-400' 
                            : ''
                        } ${getPointColor(overallScore)}`}
                        style={{
                          left: `${quadrant_position.x}%`,
                          bottom: `${quadrant_position.y}%`,
                        }}
                        onClick={() => setSelectedFreelancer(
                          selectedFreelancer === freelancer.id ? null : freelancer.id
                        )}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <div className="space-y-1">
                        <div className="font-semibold">{freelancer.nome}</div>
                        <div className="text-xs text-slate-600">
                          Score: {Math.round(overallScore)} • 
                          Skills: {Math.round(quadrant_position.x)}% • 
                          Valor: {Math.round(quadrant_position.y)}%
                        </div>
                        {freelancer.cidade && (
                          <div className="text-xs text-slate-500">{freelancer.cidade}</div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>

            {/* Labels dos eixos */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-2">
              <span className="text-xs text-slate-600 bg-white px-2 py-1 rounded shadow-xs">
                Compatibilidade de Skills →
              </span>
            </div>
            <div className="absolute top-0 bottom-0 left-0 flex items-center pl-2">
              <span className="text-xs text-slate-600 bg-white px-2 py-1 rounded shadow-xs transform -rotate-90">
                Compatibilidade de Valor →
              </span>
            </div>
          </div>

          {/* Escala de cores */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs">
            <span className="text-slate-600">Score Geral:</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>80-100</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>70-79</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>60-69</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span>50-59</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detalhes do freelancer selecionado */}
      {selectedFreelancer && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-6">
            {(() => {
              const recommendation = recommendations.find(r => r.freelancer.id === selectedFreelancer);
              if (!recommendation) return null;
              
              const { freelancer, scoring } = recommendation;
              
              return (
                <div className="flex gap-4">
                  <Avatar className="w-16 h-16 shadow-md border-2 border-white">
                    <AvatarFallback className="bg-linear-to-r from-blue-100 to-purple-100 text-blue-700 text-lg font-bold">
                      {getInitials(freelancer.nome)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg">{freelancer.nome}</h3>
                        {freelancer.cidade && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <MapPin className="h-3 w-3" />
                            {freelancer.cidade}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          {Math.round(parseFloat(scoring.overall_score))}
                        </div>
                        <div className="text-xs text-slate-500">Score Geral</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-medium">Experiência & Valor</span>
                        </div>
                        <div className="text-sm text-slate-600">
                          {freelancer.experiencia_anos} anos de experiência
                          {freelancer.pretensao_salarial && (
                            <span> • R$ {freelancer.pretensao_salarial}</span>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium mb-1">Posição no Mapa</div>
                        <div className="text-sm text-slate-600">
                          Skills: {Math.round(recommendation.quadrant_position.x)}% • 
                          Valor: {Math.round(recommendation.quadrant_position.y)}%
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex gap-3">
                        <ExternalLinkButton
                          kind="portfolio"
                          url={freelancer.portfolio_url}
                          variant="pill"
                          label="Portfolio"
                          icon={ExternalLink}
                          className="text-blue-700 bg-blue-100 hover:bg-blue-200"
                          iconClassName="h-3 w-3"
                        />

                        <FreelancerLinkedInButton
                          freelancerId={freelancer.id}
                          url={freelancer.linkedin_url}
                          variant="pill"
                          label="LinkedIn"
                          className="text-blue-800 bg-blue-100 hover:bg-blue-200"
                          iconClassName="h-3 w-3"
                        />
                      </div>

                      <Button
                        asChild
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Link to={`/freelancers/${freelancer.id}?fromVaga=${vagaId}`}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Ver Perfil
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
